/** Selectable horizontal PDF.js text runs mapped to visual top-left page points.
 * The selectable unit is a PDF text run, not necessarily a full sentence. */
import { clamp, positionWithinPage } from './overlayGeometry.js';
import { matchPdfFont } from './sourceObjects.js';

export function multiplyPdfTransform(a, b) {
  return [
    a[0]*b[0]+a[2]*b[1], a[1]*b[0]+a[3]*b[1],
    a[0]*b[2]+a[2]*b[3], a[1]*b[2]+a[3]*b[3],
    a[0]*b[4]+a[2]*b[5]+a[4], a[1]*b[4]+a[3]*b[5]+a[5],
  ];
}

export function textSelectionRuns(textContent, viewport, bounds) {
  const items = textContent?.items || [];
  const results = [];
  for (let index=0; index<items.length; index++) {
    const run = items[index];
    if (!run.str?.trim() || !Array.isArray(run.transform) || !Number.isFinite(run.width)) continue;
    const m = multiplyPdfTransform(viewport.transform, run.transform);
    // Rotated/vertical text requires polygon selection; v0.6 only supports horizontal runs.
    const glyphHeight = Math.hypot(m[2], m[3]);
    if (!Number.isFinite(glyphHeight) || glyphHeight < 2 || Math.abs(m[1]) > glyphHeight*0.2) continue;
    const asc = textContent.styles?.[run.fontName]?.ascent;
    const ascent = Number.isFinite(asc) ? asc : 0.85;
    const width = Math.max(8, Math.abs(run.width * (viewport.scale || 1)));
    const height = Math.max(8, glyphHeight * 1.16);
    const raw = {x: m[4]-1, y:m[5]-glyphHeight*ascent-1, width:width+3, height:height+2};
    if (raw.x >= bounds.width || raw.y >= bounds.height || raw.x+raw.width <= 0 || raw.y+raw.height <= 0) continue;
    const rect = positionWithinPage(raw,bounds);
    const matched=matchPdfFont(run.fontName,textContent.styles?.[run.fontName]?.fontFamily);
    results.push({id:`run-${index}`,text:run.str, ...rect,
      fontSize:Math.round(clamp(glyphHeight,6,144)), ...matched});
  }
  return results;
}

export function rectFromPoints(start,end,bounds) {
  const x=clamp(Math.min(start.x,end.x),0,bounds.width);
  const y=clamp(Math.min(start.y,end.y),0,bounds.height);
  const width=clamp(Math.abs(end.x-start.x),0,bounds.width-x);
  const height=clamp(Math.abs(end.y-start.y),0,bounds.height-y);
  return {x,y,width,height};
}
export function hasOriginalMasks(overlays=[]) {
  return overlays.some(o=>(!o.hidden || o.removedSource) && o.originalRegion && o.originalRegion.width>0 && o.originalRegion.height>0);
}

/** Group adjacent runs into logical editable lines rather than tiny text pieces.
 * PDF content streams often split a heading into several unrelated draw calls. */
export function groupTextLines(runs=[]) {
  const groups=[];
  for(const item of [...runs].sort((a,b)=>a.y-b.y||a.x-b.x)) {
    const last=groups.at(-1);
    if(last) {
      const vertical=Math.abs((item.y+item.height/2)-(last.y+last.height/2));
      const gap=item.x-(last.x+last.width);
      const sameStyle=last.fontFamily===item.fontFamily && last.bold===item.bold &&
        last.italic===item.italic && Math.abs(last.fontSize-item.fontSize)<=2;
      if(sameStyle && vertical<=Math.max(3,Math.min(last.height,item.height)*0.38)
          && gap>=-Math.min(3,item.fontSize*0.18) && gap<=Math.max(5,item.fontSize*1.25)) {
        const space=gap>Math.max(1.5,item.fontSize*0.14) && !/\s$/.test(last.text) && !/^\s/.test(item.text);
        last.text+=(space?' ':'')+item.text;
        const right=Math.max(last.x+last.width,item.x+item.width);
        const bottom=Math.max(last.y+last.height,item.y+item.height);
        last.y=Math.min(last.y,item.y);
        last.height=bottom-last.y;
        last.width=right-last.x;
        continue;
      }
    }
    groups.push({...item});
  }
  return groups;
}
